import React from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faClock,
  faUserFriends,
  faUsers,
  faBell // Ajout de l'icône pour la relance
} from '@fortawesome/free-solid-svg-icons';

const SalonInfoCards = ({ salon }) => {
  // Fonctions utilitaires pour le formatage
  const formatDate = date => {
    if (!date) return '';
    // Gestion de la date avec l'heure pour last_reminder_sent_at
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR');
  };

  const formatDateTime = dateTime => {
    if (!dateTime) return '';
    const d = new Date(dateTime);
    return `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };

  const formatTime = time => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  return (
    <Row className="g-3">
      <Col md={6}>
        <Card className="h-100 bg-light border-0">
          <Card.Body className="p-3">
            <h6 className="text-primary mb-3">Informations générales</h6>
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="text-primary me-2"
                fixedWidth
              />
              <div>
                <strong className="me-1">Date :</strong>
                <span>{formatDate(salon.date)}</span>
              </div>
            </div>
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon
                icon={faClock}
                className="text-primary me-2"
                fixedWidth
              />
              <div>
                <strong className="me-1">Horaires :</strong>
                <span>
                  {formatTime(salon.start_time)} - {formatTime(salon.end_time)}
                </span>
              </div>
            </div>
            <div className="d-flex align-items-center mb-2">
              {' '}
              {/* Changement de mb-0 à mb-2 pour espacement si last_reminder_sent_at est présent */}
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="text-primary me-2"
                fixedWidth
              />
              <div>
                <strong className="me-1">Durée des créneaux :</strong>
                <span>{salon.slot_duration} minutes</span>
              </div>
            </div>
            {salon.reminder_start_date && salon.reminder_frequency && (
              <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon
                  icon={faBell}
                  className="text-primary me-2"
                  fixedWidth
                />
                <div>
                  <strong className="me-1">Configuration relances :</strong>
                  <span>
                    À partir du {formatDate(salon.reminder_start_date)}, tous
                    les {salon.reminder_frequency === 'daily' ? 'jours' : ''}
                    {salon.reminder_frequency === 'every_3_days'
                      ? '3 jours'
                      : ''}
                    {salon.reminder_frequency === 'every_7_days'
                      ? '7 jours'
                      : ''}
                  </span>
                </div>
              </div>
            )}
            {salon.last_reminder_sent_at && (
              <div className="d-flex align-items-center">
                <FontAwesomeIcon
                  icon={faBell} // Utilisation de l'icône de cloche pour la relance
                  className="text-primary me-2" // Couleur différente pour la relance
                  fixedWidth
                />
                <div>
                  <strong className="me-1">Dernière relance envoyée :</strong>
                  <span>{formatDateTime(salon.last_reminder_sent_at)}</span>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
      <Col md={6}>
        <Card className="h-100 bg-light border-0">
          <Card.Body className="p-3">
            <h6 className="text-primary mb-3">Informations complémentaires</h6>

            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon
                icon={faClock}
                className="text-primary me-2"
                fixedWidth
              />
              <div>
                <strong className="me-1">Pause déjeuner :</strong>
                <span>
                  {formatTime(salon.lunch_break_start_time)} (
                  {salon.lunch_break_duration} minutes)
                </span>
              </div>
            </div>

            {salon.registration_start_date && salon.registration_end_date && (
              <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="text-primary me-2"
                  fixedWidth
                />
                <div>
                  <strong className="me-1">Période des vœux :</strong>
                  <span>
                    Du {formatDate(salon.registration_start_date)} au{' '}
                    {formatDate(salon.registration_end_date)}
                  </span>
                </div>
              </div>
            )}
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon
                icon={faUserFriends}
                className="text-primary me-2"
                fixedWidth
              />
              <div>
                <strong className="me-1">Participants partenaires :</strong>
                <span>
                  {salon.partner_participants_count || 0}{' '}
                  {(salon.partner_participants_count || 0) > 1
                    ? 'personnes'
                    : 'personne'}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon
                icon={faUsers}
                className="text-primary me-2"
                fixedWidth
              />
              <div>
                <strong className="me-1">Participants adhérents :</strong>
                <span>
                  {salon.company_participants_count || 0}{' '}
                  {(salon.company_participants_count || 0) > 1
                    ? 'personnes'
                    : 'personne'}
                </span>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {salon.breaks && salon.breaks.length > 0 && (
        <Col md={12}>
          <Card className="bg-light border-0">
            <Card.Body className="p-3">
              <h6 className="text-primary mb-3">Pauses additionnelles</h6>
              <div className="mb-2">
                <p className="small mb-3">
                  Ces plages horaires définissent des périodes pendant
                  lesquelles des pauses seront ajoutées lors de la planification
                  automatique. Chaque pause a une durée de {salon.slot_duration}{' '}
                  minutes (durée d'un créneau standard).
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {salon.breaks.map((breakItem, index) => (
                  <Badge key={index} bg="info" className="p-2">
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {formatTime(breakItem.start_time)} -{' '}
                    {formatTime(breakItem.end_time)}
                  </Badge>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
};

SalonInfoCards.propTypes = {
  salon: PropTypes.object.isRequired
};

export default SalonInfoCards;
